import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { skillService } from '../../../../services/skillService';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const SkillForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    icon: '',
    title: '',
    description: '',
    technologies: '',
    displayOrder: 0
  });

  useEffect(() => {
    if (isEdit) {
      fetchSkill();
    }
  }, [id]);

  const fetchSkill = async () => {
    try {
      setLoading(true);
      const data = await skillService?.getSkillById(id);
      setFormData({
        icon: data?.icon || '',
        title: data?.title || '',
        description: data?.description || '',
        technologies: data?.technologies?.join(', ') || '',
        displayOrder: data?.displayOrder || 0
      });
    } catch (err) {
      console.error('Error fetching skill:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        technologies: formData?.technologies?.split(',')?.map(t => t?.trim())?.filter(Boolean),
        displayOrder: parseInt(formData?.displayOrder) || 0
      };

      if (isEdit) {
        await skillService?.updateSkill(id, submitData);
      } else {
        await skillService?.createSkill(submitData);
      }
      navigate('/admin/dashboard/skills');
    } catch (err) {
      console.error('Error saving skill:', err);
      setError(err?.message || 'Failed to save skill');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Edit Skill' : 'New Skill'} - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard/skills">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">
                  {isEdit ? 'Edit Skill' : 'New Skill'}
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border p-6 md:p-8 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                placeholder="Enter skill title"
                value={formData?.title}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="icon" className="block text-sm font-medium text-foreground mb-2">
                Icon Name * (Lucide icon name)
              </label>
              <Input
                id="icon"
                name="icon"
                type="text"
                placeholder="e.g., Code2, Database, Globe"
                value={formData?.icon}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Visit <a href="https://lucide.dev/icons" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">lucide.dev</a> for icon names
              </p>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                placeholder="Describe the skill"
                value={formData?.description}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label htmlFor="technologies" className="block text-sm font-medium text-foreground mb-2">
                Technologies (comma-separated)
              </label>
              <Input
                id="technologies"
                name="technologies"
                type="text"
                placeholder="React, Vue, Angular"
                value={formData?.technologies}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="displayOrder" className="block text-sm font-medium text-foreground mb-2">
                Display Order
              </label>
              <Input
                id="displayOrder"
                name="displayOrder"
                type="number"
                placeholder="0"
                value={formData?.displayOrder}
                onChange={handleChange}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower numbers appear first
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                iconName={loading ? 'Loader2' : 'Save'}
                iconPosition="left"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Skill' : 'Create Skill'}
              </Button>
              <Link to="/admin/dashboard/skills">
                <Button type="button" variant="outline" disabled={loading}>
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};

export default SkillForm;